/*
 * Accessibility JS patches for starterbakery.com — from the 2026-07-31 audit.
 * Install as a theme asset alongside a11y-patches.css (see fixes/README.md).
 *
 * Follows the same additive-patch pattern as the theme's existing
 * a11y-scripts.js: each fix is self-contained, idempotent, and guarded so it
 * no-ops where its target is absent. Fixes that belong at the source (theme
 * Liquid, page content, a11y-scripts.js itself) are marked DURABLE-FIX in
 * fixes/accessibility-remediation.md — this file keeps the site clean until
 * those land, and covers markup generated at runtime that Liquid can't reach.
 */
(function () {
  'use strict';

  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ---------------------------------------------------------------- *
   * aria-allowed-attr (critical) — announcement slider slides get
   * aria-setsize/aria-posinset, which are not permitted on
   * role="group", plus a broken aria-label (" of 3"). Replace with a
   * valid "N of M" label. DURABLE-FIX: a11y-scripts.js sets these.
   * ---------------------------------------------------------------- */
  function fixAnnouncementSlides() {
    var root = document.getElementById('AnnouncementSlider');
    if (!root) return;
    var applying = false;
    function apply() {
      if (applying) return;
      applying = true;
      try {
        var slides = root.querySelectorAll('[role="group"][aria-roledescription="slide"]');
        for (var i = 0; i < slides.length; i++) {
          var s = slides[i];
          if (s.hasAttribute('aria-setsize')) s.removeAttribute('aria-setsize');
          if (s.hasAttribute('aria-posinset')) s.removeAttribute('aria-posinset');
          var label = (i + 1) + ' of ' + slides.length;
          if (s.getAttribute('aria-label') !== label) s.setAttribute('aria-label', label);
        }
      } finally {
        applying = false;
      }
    }
    apply();
    new MutationObserver(apply).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-setsize', 'aria-posinset', 'aria-label'],
    });
  }

  /* ---------------------------------------------------------------- *
   * aria-valid-attr (critical) — collapsible triggers (footer link
   * lists, mobile nav) get a nonstandard aria-open attribute at
   * runtime; aria-expanded on the same buttons already carries the
   * state. Strip aria-open wherever it appears.
   * ---------------------------------------------------------------- */
  function fixAriaOpen() {
    function sweep(scope) {
      var els = (scope.querySelectorAll ? scope : document).querySelectorAll('[aria-open]');
      for (var i = 0; i < els.length; i++) els[i].removeAttribute('aria-open');
    }
    sweep(document);
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.type === 'attributes' && m.target.hasAttribute('aria-open')) {
          m.target.removeAttribute('aria-open');
        }
      }
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-open'] });
  }

  /* ---------------------------------------------------------------- *
   * button-name (critical) — the newsletter Subscribe button's text
   * span is hidden at some widths, leaving only an aria-hidden SVG.
   * DURABLE-FIX: add aria-label in the newsletter section Liquid.
   * ---------------------------------------------------------------- */
  function fixSubscribeButton() {
    var btns = document.querySelectorAll('button[name="commit"]:not([aria-label])');
    for (var i = 0; i < btns.length; i++) btns[i].setAttribute('aria-label', 'Subscribe');
  }

  /* ---------------------------------------------------------------- *
   * listitem (serious, 285 nodes) — the nav <ul> carries
   * role="navigation", which overrides its implicit list role and
   * orphans every <li>. Move the landmark to the ul's parent so the
   * list semantics return and the navigation landmark survives.
   * DURABLE-FIX: in the header section Liquid, wrap the ul in <nav>
   * and remove role/aria-label from the ul itself.
   * ---------------------------------------------------------------- */
  function fixNavListRole() {
    var lists = document.querySelectorAll('ul[role="navigation"]');
    for (var i = 0; i < lists.length; i++) {
      var ul = lists[i];
      var parent = ul.parentElement;
      if (parent && !parent.getAttribute('role')) {
        parent.setAttribute('role', 'navigation');
        var label = ul.getAttribute('aria-label');
        if (label && !parent.getAttribute('aria-label')) parent.setAttribute('aria-label', label);
      }
      ul.removeAttribute('role');
      ul.removeAttribute('aria-label');
    }
  }

  /* ---------------------------------------------------------------- *
   * frame-title (serious) — map/video embeds without an accessible
   * name. DURABLE-FIX: put descriptive titles in the page content
   * (e.g. "Map of Starter Bakery Rockridge"); this fallback keeps the
   * rule clean meanwhile.
   * ---------------------------------------------------------------- */
  function fixFrameTitles() {
    var frames = document.querySelectorAll('iframe:not([title]):not([aria-label])');
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var src = f.getAttribute('src') || '';
      f.setAttribute('title', /google\.[^/]+\/maps|maps\.google/.test(src) ? 'Location map' : 'Embedded content');
    }
  }

  /* ---------------------------------------------------------------- *
   * scrollable-region-focusable (serious) — theme JS wraps wide
   * tables (privacy policy cookie tables) in scrollable .table-wrapper
   * divs that keyboard users can't reach. Make them focusable regions.
   * ---------------------------------------------------------------- */
  function fixTableWrappers() {
    var wraps = document.querySelectorAll('.table-wrapper:not([tabindex])');
    for (var i = 0; i < wraps.length; i++) {
      var w = wraps[i];
      w.setAttribute('tabindex', '0');
      w.setAttribute('role', 'region');
      if (!w.getAttribute('aria-label')) {
        // Prefer the nearest preceding heading as the region name.
        var label = 'Data table';
        var el = w;
        while (el && el.previousElementSibling) {
          el = el.previousElementSibling;
          if (/^H[1-6]$/.test(el.tagName)) { label = el.textContent.replace(/\s+/g, ' ').trim() || label; break; }
        }
        w.setAttribute('aria-label', label);
      }
    }
  }

  /* ---------------------------------------------------------------- *
   * link-name + list (serious, privacy policy) — Google-Docs paste
   * artifacts in page content: empty <a> elements with no accessible
   * name, and <li role="presentation"> that void the parent list.
   * DURABLE-FIX: clean the page HTML; this covers it until then.
   * ---------------------------------------------------------------- */
  function fixContentArtifacts() {
    var anchors = document.querySelectorAll('.rte a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (a.textContent.trim() === '' && !a.querySelector('img, svg') &&
          !a.getAttribute('aria-label') && !a.id && !a.getAttribute('name')) {
        a.parentNode.removeChild(a);
      }
    }
    var items = document.querySelectorAll('.rte li[role="presentation"]');
    for (var j = 0; j < items.length; j++) items[j].removeAttribute('role');
  }

  /* ---------------------------------------------------------------- *
   * Pass D — newsletter popup focus containment: keep Tab inside the
   * active modal (the theme's trapFocus can lose the cycle at the
   * modal edges). Escape/close behavior is untouched.
   * ---------------------------------------------------------------- */
  function fixModalFocusTrap() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var modal = document.querySelector('.modal.modal--is-active');
      if (!modal) return;
      var focusables = modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      if (!modal.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    });
  }

  whenReady(function () {
    fixAnnouncementSlides();
    fixAriaOpen();
    fixSubscribeButton();
    fixNavListRole();
    fixFrameTitles();
    fixTableWrappers();
    fixContentArtifacts();
    fixModalFocusTrap();
    // Sections re-render in the theme editor and on app events.
    document.addEventListener('shopify:section:load', function () {
      fixSubscribeButton();
      fixNavListRole();
      fixFrameTitles();
      fixTableWrappers();
    });
  });
})();
